import { useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Manifesto", href: "#manifesto" },
  { label: "Science", href: "#science" },
  { label: "Team", href: "#team" },
  { label: "Careers", href: "/careers" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("#") && location.pathname !== "/") {
      navigate(`/${href}`);
      return;
    }
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#080631]/45 border-b border-[#10B8B1]/15"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <button
          onClick={() => (location.pathname === "/" ? window.scrollTo({ top: 0, behavior: "smooth" }) : navigate("/"))}
          className="flex items-center"
          data-testid="nav-logo"
        >
          <img src="/assets/qelp-logo-dark.webp" alt="Qelp AI" className="h-7 md:h-8 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-[#10B8B1] transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <div className="flex items-center gap-2" data-testid="nav-status">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B8B1] animate-qelp-pulse" />
            <span className="font-mono-qelp text-[10px] tracking-[0.18em] text-slate-500 uppercase">
              Research: Active
            </span>
          </div>
          <button
            onClick={() => go("#contact")}
            className="qelp-btn-ghost rounded-full px-5 py-2 font-mono-qelp text-[11px] tracking-[0.15em] uppercase"
            data-testid="nav-cta-contact"
          >
            Get in Touch
          </button>
        </div>

        <button
          className="md:hidden text-slate-300"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#10B8B1]/15 bg-[#080631]/95 backdrop-blur-xl px-6 py-5 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-left font-mono-qelp text-xs tracking-[0.2em] uppercase text-slate-300"
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            className="qelp-btn-ghost rounded-full px-5 py-2.5 font-mono-qelp text-[11px] tracking-[0.15em] uppercase w-fit"
            data-testid="nav-mobile-cta"
          >
            Get in Touch
          </button>
        </div>
      )}
    </motion.header>
  );
};

export default Navbar;
